import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { VillageViewModel } from "../../../domain/contracts";
import { LABEL_MIN_ZOOM } from "../lib/mapUtils";

type SearchResult = {
  village: VillageViewModel;
  score: number;
};

/** Zoom level applied when a village is selected via search.  One tick above the
 *  label-visibility threshold so the village name is clearly readable. */
export const SEARCH_ZOOM = LABEL_MIN_ZOOM + 1; // 15

function scoreVillage(query: string, village: VillageViewModel): number {
  const title = (village.title ?? "").toLowerCase();
  const township = (village.township ?? "").toLowerCase();
  const slug = (village.slug ?? "").toLowerCase();

  if (title === query) return 100;
  if (slug === query) return 95;
  if (title.startsWith(query)) return 80;
  if (slug.startsWith(query)) return 75;
  if (title.includes(query)) return 60;
  if (township.startsWith(query)) return 40;
  if (township.includes(query)) return 30;
  return 0;
}

export function useVillageSearch(
  villages: VillageViewModel[],
  onSelectVillage: (id: string) => void,
  setPanTarget: (target: [number, number] | null) => void,
  setZoomTarget: (zoom: number | null) => void,
) {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo<SearchResult[]>(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed || trimmed.length < 1) return [];

    const scored: SearchResult[] = [];

    for (const village of villages) {
      const searchSpace = `${village.title ?? ""} ${village.township ?? ""}`.toLowerCase();
      if (!searchSpace.includes(trimmed)) continue;

      const score = scoreVillage(trimmed, village);
      if (score > 0) {
        scored.push({ village, score });
      }
    }

    return scored
      .sort((a, b) => b.score - a.score || a.village.title.localeCompare(b.village.title))
      .slice(0, 5);
  }, [villages, query]);

  const selectResult = useCallback((village: VillageViewModel) => {
    setQuery("");
    setIsFocused(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
    onSelectVillage(village.id);
  }, [onSelectVillage]);

  const handleChange = useCallback((value: string) => {
    setQuery(value);
    setActiveIndex(-1);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  const showDropdown = isFocused && results.length > 0;

  useEffect(() => {
    if (!showDropdown) {
      setActiveIndex(-1);
      return;
    }

    setActiveIndex((current) => (current >= results.length ? results.length - 1 : current));
  }, [results.length, showDropdown]);

  const commitResult = useCallback((village: VillageViewModel) => {
    setPanTarget([village.coordinates.lat, village.coordinates.lng]);
    setZoomTarget(SEARCH_ZOOM);
    selectResult(village);
  }, [selectResult, setPanTarget, setZoomTarget]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setIsFocused(false);
      inputRef.current?.blur();
      return;
    }

    if (!showDropdown) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => {
        const nextIndex = current + 1;
        return nextIndex >= results.length ? results.length - 1 : nextIndex;
      });
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => (current <= 0 ? 0 : current - 1));
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0 && activeIndex < results.length) {
      event.preventDefault();
      commitResult(results[activeIndex].village);
    }
  }, [activeIndex, commitResult, results, showDropdown]);

  return {
    query,
    results,
    showDropdown,
    isFocused,
    activeIndex,
    inputRef,
    handleChange,
    selectResult,
    commitResult,
    clearSearch,
    setIsFocused,
    handleKeyDown,
    setActiveIndex,
  };
}